/*
 * Атрибуция источника: откуда человек пришёл.
 *
 * Два слоя, оба — ПЕРВОЕ касание (08.10.2026):
 *   • визит (sessionStorage вкладки) — первое касание этого визита;
 *   • посетитель (localStorage, 30 дней) — первое касание вообще.
 * Касание — метки utm_*, клики yclid (Директ) / gclid (Google Ads), внешний
 * referrer и страница входа. Первое касание с источником побеждает: позже
 * пришедшие метки, возврат с кассы и переходы внутри сайта его не затирают.
 * Прямой заход без источника записывается в визит только страницей входа и
 * уступает первому настоящему источнику.
 *
 * Возврат с кассы — не источник: referrer ЮMoney/ЮKassa/Робокассы/Stripe и
 * собственных доменов (astrofeel.ru, астрофил.рф) выкидывается, вход на
 * /payment/… касанием не считается. Раньше вторая покупка в той же сессии
 * получала источник yoomoney.ru / checkout.stripe.com и вход /payment/success.
 *
 * Захват идёт на импорте модуля, ДО первого рендера React (как в RefTracker),
 * иначе роутер успевает переписать адрес и метки теряются.
 *
 * Куда уходит: платёж — getVisitAttribution() (lib/paymentProvider.js),
 * регистрация — getStoredAttribution() (lib/auth.js register(), соцвход).
 */

const STORAGE_KEY = 'astrofeel_attribution';
const VISIT_KEY = 'astrofeel_visit_attribution';
// Тот же срок, что у реферального кода: месяц на раздумья.
const TTL_DAYS = 30;

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
const CLICK_KEYS = ['yclid', 'gclid'];
const OUT_KEYS = [...UTM_KEYS, ...CLICK_KEYS, 'referrer', 'landing_page'];

// Кассы и собственные домены: переход оттуда — не источник.
const PAYMENT_HOSTS = ['yoomoney.ru', 'yookassa.ru', 'robokassa.ru', 'robokassa.com',
  'stripe.com', 'stripe.network', 'checkout.stripe.com'];
const OWN_HOSTS = ['astrofeel.ru', 'xn--80aqjkjfhs.xn--p1ai', 'астрофил.рф'];
// Внутренние ссылки сайта несут utm_source=site — это не внешний источник.
const INTERNAL_UTM_SOURCES = ['site'];

function hostMatches(host, list) {
  return list.some((d) => host === d || host.endsWith(`.${d}`));
}

export function isIgnoredReferrer(referrer, ownHostname = '') {
  try {
    let host = new URL(referrer).hostname.toLowerCase();
    if (host.startsWith('www.')) host = host.slice(4);
    let own = String(ownHostname || '').toLowerCase();
    if (own.startsWith('www.')) own = own.slice(4);
    return host === own || hostMatches(host, PAYMENT_HOSTS) || hostMatches(host, OWN_HOSTS);
  } catch {
    return true; // битый referrer — считаем, что его нет
  }
}

/** Касание из адреса и referrer: { ...метки, yclid?, gclid?, referrer?, landing_page }. */
export function readTouch(loc, ref) {
  const params = new URLSearchParams((loc && loc.search) || '');
  const touch = {};
  for (const key of [...UTM_KEYS, ...CLICK_KEYS]) {
    const value = (params.get(key) || '').trim();
    if (value) touch[key] = value.slice(0, 200);
  }
  if (INTERNAL_UTM_SOURCES.includes((touch.utm_source || '').toLowerCase())) {
    for (const key of UTM_KEYS) delete touch[key];
  }
  const referrer = String(ref || '').trim().slice(0, 500);
  if (referrer && !isIgnoredReferrer(referrer, loc && loc.hostname)) touch.referrer = referrer;
  touch.landing_page = `${(loc && loc.pathname) || ''}${(loc && loc.search) || ''}`.slice(0, 500);
  return touch;
}

/** Есть ли в касании настоящий источник: метки, yclid/gclid или внешний referrer. */
export function touchHasSource(t) {
  if (!t || typeof t !== 'object') return false;
  if ([...UTM_KEYS, ...CLICK_KEYS].some((k) => t[k])) return true;
  // Записи до 08.10 могли сохранить referrer кассы — источником он не считается.
  return !!t.referrer && !isIgnoredReferrer(t.referrer);
}

function isPaymentReturnPage(loc) {
  return /^\/payment(\/|$)/.test((loc && loc.pathname) || '');
}

function pick(data) {
  const out = {};
  if (!data || typeof data !== 'object') return out;
  for (const key of OUT_KEYS) {
    const value = data[key] == null ? '' : String(data[key]).trim();
    if (value) out[key] = value;
  }
  if (out.referrer && isIgnoredReferrer(out.referrer)) delete out.referrer;
  if (/^\/payment(\/|$)/.test(out.landing_page || '') && !touchHasSource(out)) delete out.landing_page;
  return out;
}

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || typeof data !== 'object') return null;
    if (data.expiry && Date.now() > data.expiry) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function readVisit() {
  try {
    const data = JSON.parse(sessionStorage.getItem(VISIT_KEY) || 'null');
    return data && typeof data === 'object' ? data : null;
  } catch {
    return null;
  }
}

/**
 * Захват касания. Визит: первое касание с источником; прямой заход — только
 * страница входа, пока не придёт настоящий источник. Посетитель: первое
 * касание с источником на 30 дней. Страницы /payment/… (возврат с кассы)
 * не трогают ни то, ни другое.
 */
export function captureVisitAttribution(loc = typeof window !== 'undefined' ? window.location : null,
  ref = typeof document !== 'undefined' ? document.referrer : '') {
  try {
    if (!loc || isPaymentReturnPage(loc)) return;
    const touch = readTouch(loc, ref);
    const hasSource = touchHasSource(touch);

    const visit = readVisit();
    if (!visit || (!touchHasSource(visit) && hasSource)) {
      try { sessionStorage.setItem(VISIT_KEY, JSON.stringify(touch)); } catch { /* закрыт */ }
    }

    if (hasSource && !readStored()) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          ...touch,
          savedAt: new Date().toISOString(),
          expiry: Date.now() + TTL_DAYS * 24 * 60 * 60 * 1000,
        }));
      } catch { /* закрыт */ }
    }
  } catch {
    /* хранилище закрыто — платёж и регистрация уйдут без источника */
  }
}

captureVisitAttribution();

/**
 * Источник для записи платежа: первое касание визита, а если визит без
 * источника (прямой заход) — первое касание посетителя за 30 дней; без
 * обоих — страница входа визита. Только непустые поля.
 */
export function getVisitAttribution() {
  const visit = readVisit();
  if (touchHasSource(visit)) return pick(visit);
  const stored = readStored();
  if (touchHasSource(stored)) return pick(stored);
  return pick(visit);
}

/**
 * Первое касание для карточки пользователя при регистрации: посетитель за
 * 30 дней, без него — визит. Только непустые поля.
 */
export function getStoredAttribution() {
  const stored = readStored();
  if (touchHasSource(stored)) return pick(stored);
  return pick(readVisit());
}

export const ATTRIBUTION_STORAGE_KEY = STORAGE_KEY;
export const VISIT_ATTRIBUTION_STORAGE_KEY = VISIT_KEY;
